import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { PlusIcon } from '@heroicons/react/24/outline'
import { Button } from 'src/components/button'
import { PageHeader } from 'src/components/layout/page-header'
import { MainContent } from 'src/components/layout/main-content'
import { ContentPane } from 'src/components/layout/content-pane'
import { FilterPane } from 'src/components/layout/filter-pane'
import { CustomSelect } from 'src/components/custom-select'
import { SelectOption } from 'src/services/base-model'
import { organizationQueries } from 'src/services/user'
import { paymentAccountQueries } from 'src/services/payment-accounts/queries'
import { ItemCollection } from 'src/components/layout/item-collection'
import { ListToolbar } from 'src/components/layout/list-toolbar'
import { StatementListItem } from 'src/components/statements/statement-list-item'
import { Spinner } from 'src/components/spinner'
import { PaginationComponent } from 'src/components/pagination'
import { ConfirmDeleteDialog } from 'src/components/confirm-delete-dialog'
import { statementQueries } from 'src/services/statements/queries'
import { statementApi } from 'src/services/statements/api'
import {
  Statement,
  StatementStatusValues,
  statementStatusLabelKeys,
} from 'src/services/statements/models'
import { usePagination } from 'src/hooks/use-pagination'
import { useDeleteWithConfirmation } from 'src/hooks/use-delete-with-confirmation'
import { useViewMode } from 'src/hooks/use-view-mode'

// Several backend statuses share one user-facing label (e.g. uploaded + processing
// both read "Processing"), so the status filter offers labels and expands to statuses.
const STATUSES_BY_LABEL_KEY = Object.entries(statementStatusLabelKeys).reduce<
  Record<string, StatementStatusValues[]>
>((acc, [status, labelKey]) => {
  ;(acc[labelKey] ??= []).push(status as StatementStatusValues)
  return acc
}, {})

export const StatementsPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [viewMode, setViewMode] = useViewMode('statements', 'list')

  // Delete
  const { requestDelete, dialogProps } = useDeleteWithConfirmation({
    mutationFn: statementApi.remove,
    queryKey: statementQueries.all(),
  })

  // Filters
  const [statuses, setStatuses] = useState<SelectOption[]>([])
  const [paymentAccounts, setPaymentAccounts] = useState<SelectOption[]>([])
  const [organizations, setOrganizations] = useState<SelectOption[]>([])
  const [accountSearch, setAccountSearch] = useState('')
  const [orgSearch, setOrgSearch] = useState('')

  const clearFilters = () => {
    setStatuses([])
    setPaymentAccounts([])
    setOrganizations([])
  }

  const { data: accountsData, isLoading: loadingAccounts } = useQuery(
    paymentAccountQueries.list({ filters: { search: accountSearch } }),
  )
  const { data: orgsData, isLoading: loadingOrgs } = useQuery(
    organizationQueries.list({ filters: { search: orgSearch } }),
  )

  const statusOpts = useMemo(
    () =>
      Object.keys(STATUSES_BY_LABEL_KEY).map((labelKey) => ({
        label: t(labelKey),
        value: labelKey,
      })),
    [t],
  )
  const accountOpts = useMemo(
    () => accountsData?.results.map((a) => ({ label: a.name, value: a.id })) || [],
    [accountsData?.results],
  )
  const orgOpts = useMemo(
    () => orgsData?.results.map((o) => ({ label: o.name, value: o.id })) || [],
    [orgsData?.results],
  )

  // Pagination
  const { pagination, paginationCallback, goToNextPage, goToPrevPage, goToPage, resetPagination } =
    usePagination()

  useEffect(() => {
    resetPagination()
  }, [statuses, paymentAccounts, organizations, resetPagination])

  const { data: statements, isLoading } = useQuery(
    statementQueries.list({
      filters: {
        status: statuses.flatMap((o) => STATUSES_BY_LABEL_KEY[o.value] ?? []),
        paymentAccount: paymentAccounts.map((o) => o.value),
        organization: organizations.map((o) => o.value),
      },
      pagination,
      paginationCallback,
    }),
  )

  const statementsList = statements?.results || []

  return (
    <>
      <PageHeader
        title={t('statementsPage.title')}
        actions={
          <Button
            variant="primary"
            icon={<PlusIcon />}
            onClick={() => navigate('/expenses/statements/new')}
          >
            {t('statementsPage.newStatement')}
          </Button>
        }
      />
      <MainContent>
        <FilterPane title={t('common.filters')} onClear={clearFilters}>
          <h3 className="mb-2 mt-4 text-base font-semibold">{t('statementsPage.status')}</h3>
          <CustomSelect
            options={statusOpts}
            values={statuses}
            onChange={setStatuses}
            multi
            clearable
            data-testid="statement-filter-status"
          />
          <h3 className="mb-2 mt-4 text-base font-semibold">
            {t('statementsPage.paymentAccount')}
          </h3>
          <CustomSelect
            searchable
            options={accountOpts}
            values={paymentAccounts}
            onChange={setPaymentAccounts}
            multi
            clearable
            loading={loadingAccounts}
            searchFn={({ state }) => {
              setAccountSearch(state.search)
              return accountOpts
            }}
            data-testid="statement-filter-payment-account"
          />
          <h3 className="mb-2 mt-4 text-base font-semibold">{t('statementsPage.organization')}</h3>
          <CustomSelect
            searchable
            options={orgOpts}
            values={organizations}
            onChange={setOrganizations}
            multi
            clearable
            loading={loadingOrgs}
            searchFn={({ state }) => {
              setOrgSearch(state.search)
              return orgOpts
            }}
            data-testid="statement-filter-organization"
          />
        </FilterPane>
        <ContentPane>
          <ListToolbar
            count={statements?.count}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
          />
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner size="lg" />
            </div>
          ) : statementsList.length === 0 ? (
            <div className="py-12 text-center" data-testid="empty-state">
              <p className="text-text-placeholder">{t('statementsPage.emptyState')}</p>
            </div>
          ) : (
            <>
              <ItemCollection viewMode={viewMode}>
                {statementsList.map((statement: Statement) => (
                  <StatementListItem
                    key={statement.id}
                    statement={statement}
                    viewMode={viewMode}
                    onDelete={requestDelete}
                  />
                ))}
              </ItemCollection>
              <PaginationComponent
                pagination={pagination}
                isLoading={isLoading}
                onNextPage={goToNextPage}
                onPrevPage={goToPrevPage}
                onGoToPage={goToPage}
                className="mt-4"
              />
            </>
          )}
        </ContentPane>
      </MainContent>

      <ConfirmDeleteDialog {...dialogProps} />
    </>
  )
}
